import axios from 'axios';
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { ApiResponse, ERROR_CODES, HTTP_STATUS_CODES } from '../types';
import { store } from '../redux/store';
import {
  showGlobalLoading,
  hideGlobalLoading,
} from '../redux/slices/global-loading-slice';
import { DIALOG_TYPES } from '../types/enums';
import { showDialog } from '../services/dialog/dialogService';
import i18n from '@/providers/i18n';
import { config } from '@/config';

export interface ApiClientRequestConfig extends AxiosRequestConfig {
  showGlobalLoading?: boolean;
  loadingMessage?: string;
  __showGlobalLoading?: boolean;
}

function getMetadata(
  payload: Record<string, unknown>
): Record<string, unknown> {
  let token = null as string | null;
  let clientId = null as string | null;
  let userRoleSelected = null as string | null;
  let clientSubscriptionStatus = null as string | null;

  try {
    // Try to get from Redux state first
    const state = store.getState();
    const authState = state.auth?.main;
    const sessionData = authState?.sessionData;

    if (sessionData?.User) {
      userRoleSelected = authState.userRoleSelected || null;
      token = authState.token || sessionData?.Token?.TokenValue || null;
      clientId = authState.clientId || sessionData?.Client?._id || null;
      userRoleSelected = authState.userRoleSelected || null;
      clientSubscriptionStatus =
        sessionData?.Client?.SubscriptionInfo?.Status || null;
    } else {
      userRoleSelected = localStorage.getItem('userRoleSelected');
      token = localStorage.getItem('token');
      clientId = localStorage.getItem('clientId');
      clientSubscriptionStatus = localStorage.getItem(
        'clientSubscriptionStatus'
      );
    }
  } catch (error) {
    console.error('Error getting metadata from state/localStorage:', error);
  }

  const result = {
    apiStartTime: new Date(),
    currentPlatform: import.meta.env.VITE_TARGET || 'web',
    currentVersion: import.meta.env.VITE_APP_VERSION_CODE || 1,
    token: token || null,
    userRoleSelected: userRoleSelected || null,
    clientId: clientId || null,
    clientSubscriptionStatus: clientSubscriptionStatus || null,
    ...payload,
  };

  return result;
}

function handleAxiosRequest(config: InternalAxiosRequestConfig) {
  if (['post', 'put', 'patch'].includes(config.method || '')) {
    config.data = getMetadata(config.data || {});
  }
  if (['get', 'delete'].includes(config.method || '')) {
    config.params = getMetadata(config.params || {});
  }

  // Show global loading unless explicitly disabled
  const apiConfig = config as ApiClientRequestConfig;
  const shouldShowLoading = apiConfig.showGlobalLoading !== false;
  if (shouldShowLoading) {
    const message = apiConfig.loadingMessage || undefined;
    store.dispatch(showGlobalLoading(message));
    (apiConfig as ApiClientRequestConfig).__showGlobalLoading = true;
  }

  return config;
}

async function handleVersionCodeErrors(responseData: ApiResponse<unknown>) {
  if (window.cordova != null) {
    return;
  }

  const code = responseData?.errorVersionCode || 0;
  const message = responseData?.errorMessageVersionCode;

  const sessionDismissReload = sessionStorage.getItem('dismissReloadPage');
  // Prompt user to update new version
  const promptUpdateCodes = [
    ERROR_CODES.OLD_VERSION_WEB,
    ERROR_CODES.OLD_VERSION_IOS,
    ERROR_CODES.OLD_VERSION_ANDROID,
  ] as number[];
  if (promptUpdateCodes.indexOf(code) >= 0 && sessionDismissReload !== 'true') {
    const result = await showDialog({
      type: DIALOG_TYPES.WARNING,
      title: i18n.t('NewVersionAvailable.Title', { ns: 'common' }),
      description:
        message || i18n.t('NewVersionAvailable.Message', { ns: 'common' }),
      primaryButton: {
        text: i18n.t('NewVersionAvailable.UpdateNow', { ns: 'common' }),
      },
      secondaryButton: {
        text: i18n.t('Close', { ns: 'common' }),
      },
    });

    if (result) {
      window.location.reload();
    } else {
      sessionStorage.setItem('dismissReloadPage', 'true');
    }
  }

  // Force user to update new version
  const requireUpdateCodes = [
    ERROR_CODES.OLD_MIN_VERSION_WEB,
    ERROR_CODES.OLD_MIN_VERSION_IOS,
    ERROR_CODES.OLD_MIN_VERSION_ANDROID,
  ] as number[];
  if (requireUpdateCodes.indexOf(code) >= 0) {
    await showDialog({
      type: DIALOG_TYPES.WARNING,
      title: i18n.t('NewVersionAvailable.Title', { ns: 'common' }),
      description:
        message || i18n.t('NewVersionAvailable.Message', { ns: 'common' }),
      footerClassName: 'justify-center',
      primaryButton: {
        text: i18n.t('Reload', { ns: 'common' }),
        className: 'max-w-[160px]',
      },
      secondaryButton: {
        text: '',
        isShow: false,
      },
    });

    window.location.reload();
    return;
  }
}

async function handleUnexpectedErrors(
  statusCode: number,
  responseData: ApiResponse<unknown>
) {
  console.log('handleUnexpectedErrors - statusCode:', statusCode);
  console.log('handleUnexpectedErrors - responseData:', responseData);
  const errorCode = responseData?.errorCode;
  const unexpectedErrorCodes = [
    HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR,
    HTTP_STATUS_CODES.BAD_GATEWAY,
    HTTP_STATUS_CODES.SERVICE_UNAVAILABLE,
    HTTP_STATUS_CODES.GATEWAY_TIMEOUT,
  ] as number[];
  if (
    unexpectedErrorCodes.includes(statusCode) ||
    errorCode === ERROR_CODES.INVALID_CLIENT_SUBSCRIPTION_STATUS ||
    !responseData
  ) {
    await showDialog({
      type: DIALOG_TYPES.ERROR,
      title: i18n.t('SomethingWentWrong', { ns: 'subscription' }),
      description: i18n.t('SomethingWentWrongDescription', {
        ns: 'subscription',
      }),
      secondaryButton: {
        text: i18n.t('Close', { ns: 'common' }),
      },
    });
    window.location.reload();
    return;
  }
}

async function handleAxiosResponse(response: AxiosResponse) {
  console.log('handleAxiosResponse - response:', response);
  const responseData = response?.data as ApiResponse<unknown>;
  const statusCode = response?.status;

  // Step 1: handle unexpected errors
  handleUnexpectedErrors(statusCode, responseData);

  // Step 2: handle error version codes
  handleVersionCodeErrors(responseData);

  // Step 3: handle unauthorized error globally
  if (
    statusCode === HTTP_STATUS_CODES.UNAUTHORIZED ||
    statusCode === HTTP_STATUS_CODES.FORBIDDEN
  ) {
    localStorage.removeItem('sessionData');
    localStorage.removeItem('token');
    window.location.href = '/';
  }
}

const axiosInstance = axios.create({
  baseURL: config.pteApiUrl,
  timeout: 1000 * 180, // 3 minutes timeout
  headers: {},
});

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return handleAxiosRequest(config);
  },
  (error: AxiosError) => Promise.reject(error)
);
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Hide global loading if it was shown for this request
    if ((response.config as ApiClientRequestConfig).__showGlobalLoading) {
      store.dispatch(hideGlobalLoading());
    }
    handleAxiosResponse(response);
    return response;
  },
  async (error: AxiosError) => {
    // Hide global loading if it was shown for this request
    if ((error.config as ApiClientRequestConfig).__showGlobalLoading) {
      store.dispatch(hideGlobalLoading());
    }
    const response = error.response as AxiosResponse;
    await handleAxiosResponse(response);

    if (!response) {
      return Promise.reject(error);
    }

    response.data = response.data || ({} as ApiResponse<unknown>);
    response.data.statusCode = error.status || 500;
    response.data.statusText = error.message;
    return Promise.resolve(response);
  }
);

const apiClient = {
  get: async function <T>(
    url: string,
    config?: ApiClientRequestConfig
  ): Promise<T> {
    const res = await axiosInstance.get<T>(url, config);
    return res.data;
  },
  post: async function <T>(
    url: string,
    data?: unknown,
    config?: ApiClientRequestConfig
  ): Promise<T> {
    const res = await axiosInstance.post<T>(url, data, config);
    console.log('res.data from post:', res.data);
    return res.data;
  },
  put: async function <T>(
    url: string,
    data?: unknown,
    config?: ApiClientRequestConfig
  ): Promise<T> {
    const res = await axiosInstance.put<T>(url, data, config);
    return res.data;
  },
  delete: async function <T>(
    url: string,
    config?: ApiClientRequestConfig
  ): Promise<T> {
    const res = await axiosInstance.delete<T>(url, config);
    return res.data;
  },
};

export default apiClient;
